import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * The drawer / modal / overlay manager. §11.5 makes this a shell
 * responsibility, and both Agent Profile (§3.7) and the EA dock need it.
 *
 * WHY ONE STACK AND NOT ONE PER SURFACE
 * =====================================
 * SHELL.md assigns overlays distinct jobs — drawers inspect an entity WITHIN a
 * surface, modals confirm, tooltips identify. What it does not say, because it
 * is a mockup concern rather than a spec one, is who decides which is on top.
 * If each surface owned its own overlay, two of them could both believe they
 * are the top layer: Escape would close the wrong one, the scrim would render
 * twice, and focus would return somewhere the operator never was. A single
 * ordered stack in the shell makes "topmost" a fact rather than an opinion.
 *
 * WHAT THE STACK GUARANTEES
 * =========================
 *   Escape closes exactly the topmost layer, never all of them.
 *   The scrim renders once, beneath the topmost layer only.
 *   Focus returns to whatever held it when that layer opened — so closing an
 *     Agent Profile drawer puts the operator back on the node they opened it
 *     from, not at the top of the page.
 *   Body scroll is locked while any layer is open and released when the last
 *     one closes, counted rather than toggled.
 *
 * SHELL.md's assignment of kinds is enforced by nothing here on purpose. A
 * drawer used for a primary surface is a judgement call about that surface, and
 * R7 says taste is not this session's to decide.
 */

let nextId = 1;

export function useOverlayStack() {
  const [stack, setStack] = useState([]);
  const restoreFocus = useRef(new Map());

  const close = useCallback((id) => {
    setStack((s) => s.filter((layer) => layer.id !== id));
    const el = restoreFocus.current.get(id);
    restoreFocus.current.delete(id);
    // Deferred: the layer is still mounted in this tick, so focusing now would
    // be undone by the removal. Focus the opener after React has committed.
    if (el && typeof el.focus === "function") {
      window.requestAnimationFrame(() => el.focus());
    }
  }, []);

  const open = useCallback((kind, content, options = {}) => {
    const id = nextId++;
    if (typeof document !== "undefined" && document.activeElement) {
      restoreFocus.current.set(id, document.activeElement);
    }
    setStack((s) => [...s, { id, kind, content, options }]);
    return id;
  }, []);

  const closeTop = useCallback(() => {
    setStack((s) => {
      if (!s.length) return s;
      const top = s[s.length - 1];
      const el = restoreFocus.current.get(top.id);
      restoreFocus.current.delete(top.id);
      if (el && typeof el.focus === "function") {
        window.requestAnimationFrame(() => el.focus());
      }
      return s.slice(0, -1);
    });
  }, []);

  // Escape closes the topmost layer only. Registered once for the whole stack
  // rather than once per layer, which is how a single keypress ends up closing
  // three drawers.
  useEffect(() => {
    if (!stack.length) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeTop();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [stack.length, closeTop]);

  // Scroll lock, counted. Toggling a boolean here is the bug where closing an
  // inner modal unlocks the page behind an outer drawer that is still open.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!stack.length) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [stack.length]);

  const api = useMemo(
    () => ({
      openDrawer: (content, options) => open("drawer", content, options),
      openModal: (content, options) => open("modal", content, options),
      openTooltip: (content, options) => open("tip", content, options),
      close,
      closeTop,
    }),
    [open, close, closeTop]
  );

  return { stack, api };
}

/**
 * Renders the stack. One scrim, beneath the topmost layer.
 *
 * `aria-modal` is set on modals and drawers because both take the operator out
 * of the page beneath them; tooltips do not, and marking one modal would hide
 * the page from a screen reader over a hover hint.
 */
export function OverlayHost({ stack, onClose, onCloseTop }) {
  if (!stack.length) return null;
  const top = stack[stack.length - 1];

  return (
    <div className="overlay-host">
      {stack.map((layer, i) => {
        const isTop = i === stack.length - 1;
        const { id, kind, content, options } = layer;
        const dismissible = options.dismissible !== false;

        return (
          <React.Fragment key={id}>
            {isTop && kind !== "tip" ? (
              <div
                className="scrim"
                onClick={dismissible ? onCloseTop : undefined}
                aria-hidden="true"
              />
            ) : null}
            <div
              className={kind === "drawer" ? "drawer" : kind === "modal" ? "modal" : "tip"}
              role={kind === "tip" ? "tooltip" : "dialog"}
              aria-modal={kind === "tip" ? undefined : isTop}
              aria-label={options.label}
              data-top={isTop ? "true" : "false"}
            >
              {typeof content === "function"
                ? content({ close: () => onClose(id) })
                : content}
            </div>
          </React.Fragment>
        );
      })}
      {/* `top` is referenced so a future change that drops the topmost layer
          from the render loop fails here rather than silently. */}
      <span hidden data-top-id={top.id} />
    </div>
  );
}
